'use client'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { getHomepageMediaAlt, getHomepageMediaUrl, type HomepageContent, type HomepageServiceCard } from './PayloadHomepageHero'

function ServiceCard({ card, mobile = false }: { card: HomepageServiceCard; mobile?: boolean }) {
  // These cards are portrait in both breakpoints. The default heroDesktop
  // rendition is 1920x900 and would be cropped a second time by object-cover.
  const image = getHomepageMediaUrl(card.image, 'heroMobile') || getHomepageMediaUrl(card.image, 'original')

  if (!image) return null

  return (
    <Link
      href={card.href}
      className={`group relative flex shrink-0 flex-col justify-between overflow-hidden bg-[#10223a] text-white ${mobile ? 'h-[380px] w-[280px] rounded-[18px] px-[22px] pb-6 pt-[22px]' : 'h-[540px] w-[400px] rounded-[20px] p-8'}`}
    >
      <Image
        src={image}
        alt={getHomepageMediaAlt(card.image)}
        fill
        unoptimized
        sizes={mobile ? '280px' : '400px'}
        className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
      />
      <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-[45%] from-transparent to-[rgba(16,34,58,0.86)]" />
      <span className={`relative z-10 flex shrink-0 items-center justify-center rounded-full bg-white text-[#005aa9] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${mobile ? 'h-10 w-10 self-end' : 'h-12 w-12 self-end'}`}>
        <ArrowUpRight aria-hidden="true" size={20} strokeWidth={1.8} />
      </span>
      <span className="relative z-10 flex flex-col gap-2">
        <span className={`font-extrabold leading-[1.08] tracking-[-0.03em] ${mobile ? 'text-2xl' : 'text-[32px]'}`}>{card.title}</span>
        <span className={`leading-[1.45] text-white/90 ${mobile ? 'text-[15px]' : 'text-[17px]'}`}>{card.description}</span>
      </span>
    </Link>
  )
}

export default function PayloadServicesFilmstrip({ content }: { content: HomepageContent }) {
  const sectionRef = useRef<HTMLElement>(null)
  const desktopRef = useRef<HTMLDivElement>(null)
  const mobileRef = useRef<HTMLDivElement>(null)
  const [autoplayActive, setAutoplayActive] = useState(true)
  const [isVisible, setIsVisible] = useState(false)
  const cards = content.servicesSection?.cards || []
  const autoplaySeconds = Math.max(0, Number(content.servicesSection?.autoplaySeconds ?? 30))
  const hasPortraitImage = (card: HomepageServiceCard) =>
    getHomepageMediaUrl(card.image, 'heroMobile') || getHomepageMediaUrl(card.image, 'original')
  const desktopCards = cards.filter((card) => card.showOnDesktop !== false && hasPortraitImage(card))
  const mobileCards = cards.filter((card) => card.showOnMobile !== false && hasPortraitImage(card))

  useEffect(() => {
    const sectionElement = sectionRef.current
    if (!sectionElement) return
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.15 })
    observer.observe(sectionElement)
    return () => observer.disconnect()
  }, [])

  if (!content.servicesSection?.title || (!desktopCards.length && !mobileCards.length)) return null

  const scroll = (direction: -1 | 1) => {
    setAutoplayActive(false)
    desktopRef.current?.scrollBy({ left: direction * 420, behavior: 'smooth' })
  }

  useEffect(() => {
    if (!autoplayActive || !isVisible || autoplaySeconds <= 0 || Math.max(desktopCards.length, mobileCards.length) < 2) return

    const timerId = window.setInterval(() => {
      const isDesktop = window.matchMedia('(min-width: 768px)').matches
      const track = isDesktop ? desktopRef.current : mobileRef.current
      if (!track || track.scrollWidth <= track.clientWidth + 1) return

      const maxScroll = track.scrollWidth - track.clientWidth
      if (track.scrollLeft >= maxScroll - 2) {
        track.scrollTo({ left: 0, behavior: 'smooth' })
        return
      }

      track.scrollBy({ left: isDesktop ? 420 : 292, behavior: 'smooth' })
    }, autoplaySeconds * 1000)

    return () => window.clearInterval(timerId)
  }, [autoplayActive, autoplaySeconds, desktopCards.length, isVisible, mobileCards.length])

  return (
    <section
      ref={sectionRef}
      className="overflow-hidden bg-white py-16 md:py-[120px]"
      aria-label={content.servicesSection.eyebrow || 'I nostri servizi'}
      onPointerDownCapture={() => setAutoplayActive(false)}
      onClickCapture={() => setAutoplayActive(false)}
      onFocusCapture={() => setAutoplayActive(false)}
      onKeyDownCapture={() => setAutoplayActive(false)}
      onWheelCapture={() => setAutoplayActive(false)}
    >
      <div className="w-full">
      <div className="homepage-content-container">
      <div className="mb-7 flex flex-col items-start md:mb-12 md:flex-row md:items-end md:justify-between">
        <div className="max-w-[752px]">
          {content.servicesSection.eyebrow && <p className="mb-2.5 text-[15px] font-bold text-[#005aa9] md:mb-[14px] md:text-[17px]">{content.servicesSection.eyebrow}</p>}
          <h2 className="text-[32px] font-extrabold leading-[1.06] tracking-[-0.018em] text-[#10223a] md:text-[56px] md:leading-[1.04] md:tracking-[-1.68px]">{content.servicesSection.title}</h2>
        </div>
        {desktopCards.length > 0 && <div className="hidden shrink-0 gap-2.5 md:flex">
          <button type="button" aria-label="Scorri i servizi a sinistra" onClick={() => scroll(-1)} className="flex h-[52px] w-[52px] items-center justify-center rounded-full border-[1.5px] border-[#005aa9] text-[#005aa9] transition-colors hover:bg-[#005aa9]/5">
            <ArrowLeft size={20} aria-hidden="true" />
          </button>
          <button type="button" aria-label="Scorri i servizi a destra" onClick={() => scroll(1)} className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#005aa9] text-white transition-colors hover:bg-[#004b8e]">
            <ArrowRight size={20} aria-hidden="true" />
          </button>
        </div>}
      </div>
      </div>
      <div className="homepage-content-container">
      {desktopCards.length > 0 && <div ref={desktopRef} className="homepage-edge-track hidden snap-x snap-mandatory gap-5 overflow-x-auto pb-1 md:flex [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {desktopCards.map((card, index) => <div key={`${card.title}-${index}`} className="snap-start"><ServiceCard card={card} /></div>)}
      </div>}
      {mobileCards.length > 0 && <div ref={mobileRef} className="homepage-edge-track flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 md:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {mobileCards.map((card, index) => <div key={`${card.title}-${index}`} className="snap-start"><ServiceCard card={card} mobile /></div>)}
      </div>}
      </div>
      </div>
    </section>
  )
}
